import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import { getPredefinedGroupLabel } from "@common/constants/domains/accounting/accountGroups";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { TypeMappingRow } from "@common/helpers/chartOfAccountsTemplateTypeMapping";
import { Picker } from "@design-system/Picker/Picker.lib";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import {
    EDITOR_TYPE_CLASS_GROUPS,
    toTypePickerSections,
} from "../../editor/editorTypeOptions";
import editorStyles from "../../editor/ChartOfAccountsTemplateEditor.module.scss";
import styles from "../ChartOfAccountsTemplateTypeMapping.module.scss";

export interface TypeMappingCallbacks {
    onTypeChange: (params: { rowId: string; type: AccountType }) => void;
}

const typeSections = toTypePickerSections(EDITOR_TYPE_CLASS_GROUPS);

export const TypeMappingTypeCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, TypeMappingRow, unknown>) => {
        const { id, type, isTopLevel } = row.original;
        const { callbacks } = useViewTableMeta<TypeMappingCallbacks, never>(
            table,
        );

        if (!isTopLevel) {
            if (type == null) {
                return null;
            }
            return (
                <ViewTableCell className={styles.typeCell}>
                    <Tooltip content="Inherited from its top-level group. You can move it to another type later in the template editor.">
                        <span className={styles.childType}>
                            {getPredefinedGroupLabel(type)}
                        </span>
                    </Tooltip>
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell captureClicks className={styles.typeCell}>
                <span
                    className={editorStyles.editorCellAnchor}
                    data-editor-cell={`${id}:type`}
                >
                    <Picker.Select
                        sections={typeSections}
                        searchable
                        trigger="cell"
                        chevron="always"
                        className={styles.typeTrigger}
                        value={type}
                        onValueChange={(nextType: AccountType | null) => {
                            if (nextType !== null && nextType !== type) {
                                callbacks?.onTypeChange({
                                    rowId: id,
                                    type: nextType,
                                });
                            }
                        }}
                        placeholder="Select type"
                        searchPlaceholder="Search types…"
                        emptyLabel="No matching types"
                    />
                </span>
            </ViewTableCell>
        );
    },
);

TypeMappingTypeCell.displayName = "TypeMappingTypeCell";
